import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { ScoreboardSectionProps, MeetingMetric, User, ScoreboardCadence } from '../../types'
import WinTheWeekBanner from './WinTheWeekBanner'
import EmptyState from '../shared/EmptyState'
import AddMetricModal from '../scoreboard/AddMetricModal'
import EditMetricModal from '../scoreboard/EditMetricModal'
import { formatMetricValue } from '../scoreboard/formatters'
import { computeOnTarget, computeWon } from '../scoreboard/winRules'
import { useSaveQueue } from '../scoreboard/useSaveQueue'
import { MetricValueInput, WonLostToggle } from '../scoreboard/cellControls'
import InlineTargetCell from '../scoreboard/InlineTargetCell'
import ScoreboardRowMenu from './ScoreboardRowMenu'
import AddTopicModal, { type TopicCreatePayload, type TopicSubmitHandlers } from './TopicModal'
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  type DraggableProvidedDraggableProps,
  type DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd'

function formatWeekDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function unitToFormat(unit: string): 'currency' | 'percentage' | 'number' {
  if (unit === '$') return 'currency'
  if (unit === '%') return 'percentage'
  return 'number'
}

function formatValue(value: number | null, metric: MeetingMetric): string {
  const fmt = (metric.format as 'currency' | 'percentage' | 'number' | null | undefined) || unitToFormat(metric.unit)
  return formatMetricValue(value, fmt, metric.decimal_places ?? 0, '—', metric.abbreviate_large_numbers ?? true)
}

const cellKey = (metricId: number, weekEnding: string) => `${metricId}:${weekEnding}`

type ViewMode = 'numbers' | 'wonlost' | 'both'

// Matches the team Scoreboard page's cadence tab group (task #462).
const CADENCE_OPTIONS: { value: ScoreboardCadence; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]

const CADENCE_PERIOD_NOUN: Record<ScoreboardCadence, string> = {
  weekly: 'Week',
  monthly: 'Month',
  quarterly: 'Quarter',
  annual: 'Year',
}

// Look-back window presets for non-weekly tabs (task #472); matches the
// backend whitelist and the team Scoreboard page's duration dropdown.
const PERIOD_COUNT_OPTIONS = [4, 8, 13, 26, 52]

// How many weekly columns the in-meeting grid renders (task #707), current
// week included. Deliberately shorter than the non-weekly list: weekly past
// cells are editable, so every rendered week must sit inside the write
// window save_metric_entry accepts. Mirrors the backend whitelist.
const WEEKLY_PERIOD_COUNT_OPTIONS = [4, 8, 12]

function periodCountOptionsFor(cadence: ScoreboardCadence): number[] {
  return cadence === 'weekly' ? WEEKLY_PERIOD_COUNT_OPTIONS : PERIOD_COUNT_OPTIONS
}

function periodNounPlural(cadence: ScoreboardCadence, n: number): string {
  const noun = CADENCE_PERIOD_NOUN[cadence].toLowerCase()
  return n === 1 ? noun : `${noun}s`
}

// `all_metrics_count` is still emitted by the backend but is no longer read
// here — the show-more/show-fewer toggle was removed in task #68 so every
// active metric is rendered at once.
// hint: Structural and logic conflict. Both design and behavior differ.
export default function SectionScoreboard({ metrics: initialMetrics, won_the_week, win_count, total_count, weekly_metrics_complete, win_mode, win_threshold, meeting_id, can_facilitate, team_id, can_create_metric, can_update_metric, can_reorder_metric, categories, teamMembers, scoreboard_week_label, scoreboard_week_start_day, cadence: cadenceProp, period_count: periodCountProp, canCreateTopic, currentUserId }: ScoreboardSectionProps & { teamMembers?: User[]; canCreateTopic?: boolean; currentUserId?: number }) {
  const [viewMode, setViewMode] = useState<ViewMode>('both')
  // Local visibility toggle for the current-period column. Teams
  // reviewing trend history collapse it so the past weeks get the width.
  // Deliberately plain view state seeded from nothing: it must never be
  // derived from or re-synced with props, or the meeting page's ~10s
  // heartbeat prop refresh would flip the column back on mid-discussion.
  const [currentPeriodVisible, setCurrentPeriodVisible] = useState(true)
  // Local mirror of the team's week-label setting so a toggle click updates
  // the header dates instantly; the PUT persists it on the team and the
  // partial reload of scoreboard_data re-syncs from the server.
  const [weekLabel, setWeekLabel] = useState<'week_start' | 'week_ending'>(scoreboard_week_label ?? 'week_start')
  useEffect(() => {
    setWeekLabel(scoreboard_week_label ?? 'week_start')
  }, [scoreboard_week_label])

  const handleWeekLabelChange = useCallback((label: 'week_start' | 'week_ending') => {
    if (typeof team_id !== 'number') return
    setWeekLabel(label)
    router.put(`/teams/${team_id}/scoreboard/week_label`, {
      scoreboard_week_label: label,
    }, {
      preserveScroll: true,
      preserveState: true,
      only: ['scoreboard_data'],
    })
  }, [team_id])

  // Local mirror of the team's week-start-day setting (task #517) so the
  // dropdown reflects a change instantly; the PUT persists it and the
  // partial reload of scoreboard_data re-serializes the week columns
  // against the new boundaries (and keeps it through heartbeat refreshes).
  const [weekStartDay, setWeekStartDay] = useState<string>(scoreboard_week_start_day ?? 'monday')
  useEffect(() => {
    setWeekStartDay(scoreboard_week_start_day ?? 'monday')
  }, [scoreboard_week_start_day])

  const handleWeekStartDayChange = useCallback((day: string) => {
    if (typeof team_id !== 'number') return
    setWeekStartDay(day)
    router.put(`/teams/${team_id}/scoreboard/week_start_day`, {
      scoreboard_week_start_day: day,
    }, {
      preserveScroll: true,
      preserveState: true,
      only: ['scoreboard_data'],
    })
  }, [team_id])
  const [metricsState, setMetricsState] = useState<MeetingMetric[]>(initialMetrics)
  const [addMetricOpen, setAddMetricOpen] = useState(false)
  const [editMetricId, setEditMetricId] = useState<number | null>(null)
  // Row actions menu (task #644). Open state lives here, not in the row, so
  // opening one row's menu closes any other.
  const [openMenuMetricId, setOpenMenuMetricId] = useState<number | null>(null)
  // The metric whose row opened the "Add to The List" pop-up (task #716);
  // null while the pop-up is closed.
  const [topicModalMetric, setTopicModalMetric] = useState<MeetingMetric | null>(null)
  // Inline confirmation/error for the row menu's Create Topic action. The
  // shared flash banner sits at the top of the page, which is off-screen
  // while the user is working down the scoreboard grid.
  const [topicStatus, setTopicStatus] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const topicStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (topicStatusTimer.current) clearTimeout(topicStatusTimer.current)
  }, [])

  const showTopicStatus = useCallback((kind: 'success' | 'error', text: string) => {
    if (topicStatusTimer.current) clearTimeout(topicStatusTimer.current)
    setTopicStatus({ kind, text })
    topicStatusTimer.current = setTimeout(() => setTopicStatus(null), 6000)
  }, [])

  // Cadence tabs (task #462). The server-provided cadence is authoritative
  // once the partial reload lands; the local state gives immediate tab
  // highlight while the fetch is in flight.
  const serverCadence: ScoreboardCadence = cadenceProp || 'weekly'
  const [cadence, setCadence] = useState<ScoreboardCadence>(serverCadence)
  const isWeekly = cadence === 'weekly'

  // Period look-back window (task #472). Non-weekly tabs offer the team
  // Scoreboard page's duration presets; weekly has its own 4/8/12 list
  // (task #707) because its past cells are editable and every rendered week
  // must be inside the save endpoint's write window.
  const serverPeriods = periodCountProp || 4
  const [periods, setPeriods] = useState<number>(serverPeriods)

  // Re-sync the local tab/period state whenever the server delivers a
  // different cadence's data (e.g. a heartbeat or other partial reload).
  // Local state exists only for instant tab highlight while a fetch is in
  // flight; the server props are authoritative once they land, and letting
  // them diverge would show one cadence's labels over another's data.
  const prevServerCadenceRef = useRef(serverCadence)
  const prevServerPeriodsRef = useRef(serverPeriods)
  useEffect(() => {
    if (prevServerCadenceRef.current !== serverCadence) {
      prevServerCadenceRef.current = serverCadence
      setCadence(serverCadence)
    }
    if (prevServerPeriodsRef.current !== serverPeriods) {
      prevServerPeriodsRef.current = serverPeriods
      setPeriods(serverPeriods)
    }
  }, [serverCadence, serverPeriods])

  // Value for the weekly "Weeks shown" dropdown. Guarded so a count carried
  // over from a non-weekly tab (13/26/52) can never select a phantom option.
  const weeklyPeriods = WEEKLY_PERIOD_COUNT_OPTIONS.includes(periods) ? periods : 4

  const handleCadenceChange = useCallback((next: ScoreboardCadence) => {
    if (next === cadence) return
    setCadence(next)
    const params: Record<string, string | number> = { scoreboard_cadence: next }
    // Carry a non-default window across the tab switch when the target
    // cadence actually offers it (the weekly and non-weekly whitelists only
    // overlap on 4 and 8); anything else would be rejected server-side and
    // snap straight back to 4.
    if (periods !== 4 && periodCountOptionsFor(next).includes(periods)) params.scoreboard_periods = periods
    router.get(window.location.pathname, params, {
      preserveState: true,
      preserveScroll: true,
      only: ['scoreboard_data'],
    })
  }, [cadence, periods])

  const handlePeriodsChange = useCallback((next: number) => {
    if (next === periods) return
    setPeriods(next)
    router.get(window.location.pathname, { scoreboard_cadence: cadence, scoreboard_periods: next }, {
      preserveState: true,
      preserveScroll: true,
      only: ['scoreboard_data'],
    })
  }, [cadence, periods])

  // Re-sync local metrics state ONLY when the set of metric ids OR a
  // metric's definition (name, owner, target, format, …) changes — i.e. a
  // metric was added/removed or edited via the in-meeting modals — or the
  // server delivered a different cadence's data. Keying on these fields (+
  // cadence), not the array reference, means heartbeat/prop refreshes can't
  // clobber in-flight optimistic cell edits, while an Edit Metric save
  // (which partial-reloads scoreboard_data) resyncs the whole table so
  // on-track status and the win banner reflect the server's recompute.
  // The week-start-day is part of the key: changing it shifts every column's
  // week boundaries, so the whole table must resync from the server.
  const metricIdsKey = serverCadence + ':' + serverPeriods + ':' + (scoreboard_week_start_day ?? 'monday') + ':' + initialMetrics.map(m => [
    m.id, m.name, m.description ?? '', m.owner?.id ?? '', m.target ?? '', m.target_value,
    m.unit, m.format ?? '', m.decimal_places ?? '', m.abbreviate_large_numbers ?? '', m.goal_direction, m.comparison ?? '',
    m.frequency ?? '', m.category_id ?? '',
  ].join('|')).join(',')
  const prevIdsKeyRef = useRef(metricIdsKey)
  useEffect(() => {
    if (prevIdsKeyRef.current !== metricIdsKey) {
      prevIdsKeyRef.current = metricIdsKey
      setMetricsState(initialMetrics)
    }
  }, [metricIdsKey, initialMetrics])
  // Per-(metric, week) saving state: keyed by `${metricId}:${weekEnding}` so
  // a save on one cell shows its own indicator without disabling siblings.
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  // Per-(metric, week) debounce timers so editing one past week does not
  // cancel another cell's pending save.
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  // Latest pending save per cell so Enter can flush it immediately. The
  // entry is deleted before running, so the debounce firing afterwards (or
  // a second Enter) can't double-save the same edit.
  const pendingSaves = useRef<Record<string, () => void>>({})

  // Refs to each editable value input, keyed `${metricId}:${colIndex}` (col
  // 0 = current week, 1.. = past weeks), so Enter can move focus to the
  // same-column input on the next metric row.
  const valueInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  // Inline Target editing (task #650): per-metric saving state and the last
  // rejected save, shown next to the cell it belongs to.
  const [savingTargets, setSavingTargets] = useState<Set<number>>(new Set())
  const [targetErrors, setTargetErrors] = useState<Record<number, string>>({})
  // Outstanding target writes per metric, so a second edit keeps the
  // indicator up when the first one settles.
  const targetSaveCounts = useRef<Record<number, number>>({})
  // Which edit owns the cell (only the newest failure rolls back) and the
  // last target the server actually stored (the rollback baseline).
  const targetRevisions = useRef<Record<number, number>>({})
  const confirmedTargets = useRef<Record<number, number | null>>({})

  const markTargetSaving = useCallback((metricId: number, delta: 1 | -1) => {
    const next = Math.max(0, (targetSaveCounts.current[metricId] ?? 0) + delta)
    targetSaveCounts.current[metricId] = next
    setSavingTargets(prev => {
      if (prev.has(metricId) === (next > 0)) return prev
      const set = new Set(prev)
      if (next > 0) set.add(metricId); else set.delete(metricId)
      return set
    })
  }, [])

  // Every user-initiated write in this section (score cells, Won/Lost, the
  // inline Target edit) goes through one serialized queue: Inertia cancels
  // the active visit whenever a new one starts, so an independent request
  // would silently abort a save that is still in flight.
  const { enqueue, dispose } = useSaveQueue()
  useEffect(() => dispose, [dispose])

  const flushPendingSave = useCallback((key: string) => {
    const fn = pendingSaves.current[key]
    if (!fn) return
    delete pendingSaves.current[key]
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key])
      delete debounceTimers.current[key]
    }
    fn()
  }, [])

  // Enter in a value cell: flush the pending save, then move focus to the
  // same-column value input on the next metric row (content selected, ready
  // to type over). On the last row, just save — focus stays put (blurred).
  const handleValueEnter = useCallback((metricId: number, colIndex: number, flushKey: string, current: HTMLInputElement) => {
    flushPendingSave(flushKey)
    const idx = metricsState.findIndex(m => m.id === metricId)
    const nextMetric = idx >= 0 ? metricsState[idx + 1] : undefined
    const nextInput = nextMetric ? valueInputRefs.current[`${nextMetric.id}:${colIndex}`] : null
    if (nextInput) {
      nextInput.focus()
      // Defer select() a frame: focusing flips the cell from its formatted
      // display to the raw number input (task #565), so selecting must wait
      // for that re-render or the selection lands on the stale text.
      requestAnimationFrame(() => {
        try { nextInput.select() } catch { /* select() unsupported on some inputs */ }
      })
    } else {
      current.blur()
    }
  }, [flushPendingSave, metricsState])

  const markSaving = useCallback((key: string, on: boolean) => {
    setSavingCells(prev => {
      const next = new Set(prev)
      if (on) next.add(key); else next.delete(key)
      return next
    })
  }, [])

  // Save handler for the current-week row (value or Won/Lost). Mirrors the
  // server-side on_target compute on success so cell coloring updates
  // without a full meeting reload.
  const saveCurrentEntry = useCallback((metricId: number, key: string, value: string | null | undefined, wonTheWeek?: boolean | null) => {
    if (!meeting_id) return

    markSaving(key, true)

    const body: Record<string, unknown> = { metric_id: metricId }
    if (cadence !== 'weekly') body.cadence = cadence
    if (value !== undefined) body.value = value
    if (wonTheWeek !== undefined) body.won_the_week = wonTheWeek

    // Carry the current query string (scoreboard cadence/periods) so the
    // server-side redirect preserves the selected tab's view state instead
    // of dropping the URL back to the weekly default.
    enqueue({
      key,
      // Partial reload limited to the tiny `autosave_ack` prop (the queue's
      // default) so we don't re-fetch the whole meeting page on every
      // keystroke. UI updates come from the optimistic local state above.
      url: `/meetings/${meeting_id}/save_metric_entry${window.location.search}`,
      body,
      onSuccess: () => {
        setMetricsState(prev => prev.map(m => {
          if (m.id !== metricId) return m
          const updatedEntries = [...m.weekly_entries]
          if (updatedEntries.length > 0) {
            const currentValue = updatedEntries[0].value
            const onTarget = computeOnTarget(currentValue, m.target_value, m.goal_direction, m.comparison)
            const manual = updatedEntries[0].won_the_week_manual === true
            const won = manual
              ? updatedEntries[0].won_the_week
              : computeWon(currentValue, m.target_value, m.goal_direction, m.comparison)
            updatedEntries[0] = {
              ...updatedEntries[0],
              on_target: onTarget,
              won_the_week: won,
            }
            return {
              ...m,
              current_value: currentValue,
              on_target: onTarget,
              weekly_entries: updatedEntries,
            }
          }
          return m
        }))
      },
      onError: (detail) => {
        console.error('Failed to save metric entry:', detail)
      },
      onSettled: () => markSaving(key, false),
    })
  }, [meeting_id, markSaving, cadence, enqueue])

  // Save handler for past-week cells. Critically: the local state mutation
  // must ONLY touch that specific past week's entry — never current_value,
  // the row's on_target, won_the_week, or won_the_week_manual.
  // The automatic banner reads this entry only when its period is selected.
  const savePastEntry = useCallback((metricId: number, weekEnding: string, weekStart: string, rawValue: string) => {
    if (!meeting_id) return

    const key = cellKey(metricId, weekEnding)
    markSaving(key, true)

    const body: Record<string, unknown> = {
      metric_id: metricId,
      week_start: weekStart,
      value: rawValue === '' ? null : rawValue,
    }

    // Same query-string carry as saveCurrentEntry: keep scoreboard view
    // state (cadence/periods) intact through the save's redirect.
    enqueue({
      key,
      url: `/meetings/${meeting_id}/save_metric_entry${window.location.search}`,
      body,
      onSuccess: () => {
        setMetricsState(prev => prev.map(m => {
          if (m.id !== metricId) return m
          const updated = m.weekly_entries.map(entry => {
            if (entry.week_ending !== weekEnding) return entry
            const numVal = rawValue === '' ? null : parseFloat(rawValue)
            const onTarget = computeOnTarget(numVal, m.target_value, m.goal_direction, m.comparison)
            const manual = entry.won_the_week_manual === true
            const won = manual
              ? entry.won_the_week
              : computeWon(numVal, m.target_value, m.goal_direction, m.comparison)
            return { ...entry, value: numVal, on_target: onTarget, won_the_week: won }
          })
          return { ...m, weekly_entries: updated }
        }))
      },
      onError: (detail) => {
        console.error('Failed to save past-week metric entry:', detail)
      },
      onSettled: () => markSaving(key, false),
    })
  }, [meeting_id, markSaving, enqueue])

  const handleCurrentValueChange = useCallback((metricId: number, weekEnding: string, rawValue: string) => {
    setMetricsState(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const updatedEntries = [...m.weekly_entries]
      if (updatedEntries.length > 0) {
        const numVal = rawValue === '' ? null : parseFloat(rawValue)
        const manual = updatedEntries[0].won_the_week_manual === true
        const won = manual
          ? updatedEntries[0].won_the_week
          : computeWon(numVal, m.target_value, m.goal_direction, m.comparison)
        const onTarget = numVal === null ? null : computeOnTarget(numVal, m.target_value, m.goal_direction, m.comparison)
        updatedEntries[0] = { ...updatedEntries[0], value: numVal, on_target: onTarget, won_the_week: won }
      }
      return { ...m, weekly_entries: updatedEntries }
    }))

    const key = cellKey(metricId, weekEnding)
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key])
    pendingSaves.current[key] = () => saveCurrentEntry(metricId, key, rawValue === '' ? null : rawValue)
    debounceTimers.current[key] = setTimeout(() => flushPendingSave(key), 800)
  }, [saveCurrentEntry, flushPendingSave])

  const handlePastValueChange = useCallback((metricId: number, weekEnding: string, weekStart: string, rawValue: string) => {
    // Optimistic local update — only this past week's entry. Clearing the
    // input drops the cell back to neutral coloring (on_target null) right
    // away; the row's current-period fields stay put.
    setMetricsState(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const updated = m.weekly_entries.map(entry => {
        if (entry.week_ending !== weekEnding) return entry
        const numVal = rawValue === '' ? null : parseFloat(rawValue)
        const onTarget = rawValue === ''
          ? null
          : computeOnTarget(numVal, m.target_value, m.goal_direction, m.comparison)
        const manual = entry.won_the_week_manual === true
        const won = manual
          ? entry.won_the_week
          : computeWon(numVal, m.target_value, m.goal_direction, m.comparison)
        return {
          ...entry,
          value: numVal,
          on_target: onTarget,
          won_the_week: won,
        }
      })
      return { ...m, weekly_entries: updated }
    }))

    const key = cellKey(metricId, weekEnding)
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key])
    pendingSaves.current[key] = () => savePastEntry(metricId, weekEnding, weekStart, rawValue)
    debounceTimers.current[key] = setTimeout(() => flushPendingSave(key), 800)
  }, [savePastEntry, flushPendingSave])

  const handleWonLostToggle = useCallback((metricId: number, currentWeekEnding: string, currentVal: boolean | null, newVal: boolean) => {
    const finalVal = currentVal === newVal ? null : newVal
    setMetricsState(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const updatedEntries = [...m.weekly_entries]
      if (updatedEntries.length > 0) {
        // Clearing the override falls back to the auto-computed on-target
        // result from the entry's value — mirroring the server recompute.
        const won = finalVal !== null
          ? finalVal
          : computeWon(updatedEntries[0].value, m.target_value, m.goal_direction, m.comparison)
        updatedEntries[0] = {
          ...updatedEntries[0],
          won_the_week: won,
          won_the_week_manual: finalVal !== null,
        }
      }
      return { ...m, weekly_entries: updatedEntries }
    }))
    const key = cellKey(metricId, currentWeekEnding)
    saveCurrentEntry(metricId, key, undefined, finalVal)
  }, [saveCurrentEntry])

  // Won/Lost override for a PAST week on the weekly grid. Saves through the
  // same entry-save path with the manual flag (week_start routes it to the
  // right week); click-again-to-clear matches the current-week toggle.
  const handlePastWonLostToggle = useCallback((metricId: number, weekEnding: string, weekStart: string, currentVal: boolean | null, newVal: boolean) => {
    if (!meeting_id) return
    const finalVal = currentVal === newVal ? null : newVal
    setMetricsState(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const updated = m.weekly_entries.map(entry => {
        if (entry.week_ending !== weekEnding) return entry
        const won = finalVal !== null
          ? finalVal
          : computeWon(entry.value, m.target_value, m.goal_direction, m.comparison)
        return { ...entry, won_the_week: won, won_the_week_manual: finalVal !== null }
      })
      return { ...m, weekly_entries: updated }
    }))

    const key = cellKey(metricId, weekEnding)
    markSaving(key, true)
    // Through the shared queue like every other write here, so a Won/Lost
    // override and a target edit can't cancel one another.
    enqueue({
      key,
      url: `/meetings/${meeting_id}/save_metric_entry${window.location.search}`,
      body: {
        metric_id: metricId,
        week_start: weekStart,
        won_the_week: finalVal,
      },
      onError: (detail) => {
        console.error('Failed to save past-week Won/Lost override:', detail)
      },
      onSettled: () => markSaving(key, false),
    })
  }, [meeting_id, markSaving, enqueue])

  // Inline Target editing (task #650). Writes through the same metric UPDATE
  // endpoint the Edit Metric modal uses — authorization, validation and audit
  // logging are unchanged.
  //
  // Deliberately NOT a `scoreboard_data` partial reload like the modal's save:
  // that response re-seeds metricsState from the server, which would wipe the
  // optimistic value of a score edit still waiting in the save queue (and the
  // queued save's own success handler would then re-derive from the reverted
  // state). The target's formatting and the row's win/loss recolor are
  // recomputed locally below with the same rules the serializer uses.
  const applyLocalTarget = useCallback((metricId: number, target: number | null) => {
    setMetricsState(prev => prev.map(m => {
      if (m.id !== metricId) return m
      const entries = m.weekly_entries.map(entry => ({
        ...entry,
        on_target: computeOnTarget(entry.value, target, m.goal_direction, m.comparison),
        won_the_week: entry.won_the_week_manual === true
          ? entry.won_the_week
          : computeWon(entry.value, target, m.goal_direction, m.comparison),
      }))
      return {
        ...m,
        target,
        target_value: target,
        on_target: computeOnTarget(m.current_value, target, m.goal_direction, m.comparison),
        weekly_entries: entries,
      }
    }))
  }, [])

  const handleTargetSave = useCallback((metric: MeetingMetric, raw: string) => {
    if (can_update_metric !== true || typeof team_id !== 'number') return
    // Blank clears the target, mirroring the settings modal's handling of an
    // emptied Target field.
    const numVal = raw === '' ? null : parseFloat(raw)
    if (numVal !== null && Number.isNaN(numVal)) return
    const previous = metric.target ?? null
    if (numVal === previous) return

    // The rollback baseline is the last target the SERVER confirmed, never
    // the optimistic value a previous (possibly also failing) edit left on
    // screen.
    if (!(metric.id in confirmedTargets.current)) confirmedTargets.current[metric.id] = previous
    const rev = (targetRevisions.current[metric.id] ?? 0) + 1
    targetRevisions.current[metric.id] = rev

    applyLocalTarget(metric.id, numVal)
    setTargetErrors(prev => {
      if (!(metric.id in prev)) return prev
      const next = { ...prev }
      delete next[metric.id]
      return next
    })
    // Counted, not a flag: with two writes outstanding the first one settling
    // must not clear the indicator the second one still needs.
    markTargetSaving(metric.id, 1)

    // Through the same serialized queue as the score cells: an independent
    // visit here would cancel a weekly autosave that is still in flight (and
    // vice versa).
    enqueue({
      key: `metric:${metric.id}:target`,
      method: 'put',
      url: `/teams/${team_id}/scoreboard/metrics/${metric.id}`,
      body: { metric: { target: numVal } },
      mergeKey: 'metric',
      onSuccess: () => {
        confirmedTargets.current[metric.id] = numVal
      },
      onError: (detail) => {
        // A newer edit owns the cell now; a stale failure must not stomp it.
        if (targetRevisions.current[metric.id] !== rev) return
        // Put the last stored target (and the coloring it implied) back
        // rather than leaving a number on screen that was never saved.
        applyLocalTarget(metric.id, confirmedTargets.current[metric.id] ?? null)
        setTargetErrors(prev => ({ ...prev, [metric.id]: detail || 'Target could not be saved.' }))
      },
      onSettled: () => markTargetSaving(metric.id, -1),
    })
  }, [applyLocalTarget, can_update_metric, markTargetSaving, team_id, enqueue])

  // Drag-to-reorder (task #599). Metric order is global (Metric#position),
  // shared with the Team scoreboard page — there is deliberately no
  // per-meeting override — so the drop posts to the same team reorder
  // endpoint. That endpoint merges the submitted (visible, cadence-filtered)
  // rows back into the team's full ordered list, so metrics hidden behind the
  // current cadence tab keep their relative order.
  //
  // The local reorder is optimistic and is NOT snapped back by the meeting's
  // 10s heartbeat: the resync effect above keys on the metric id/definition
  // string, so a heartbeat carrying the pre-save order leaves local state
  // alone, and the resync fires only once the server's order actually
  // changes. That also means a heartbeat will not correct a failed save on
  // its own, which is why onError explicitly restores the pre-drag order.
  const handleDragEnd = useCallback((result: DropResult) => {
    if (typeof team_id !== 'number') return
    if (!result.destination || result.destination.index === result.source.index) return

    const previous = metricsState
    const next = Array.from(metricsState)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    setMetricsState(next)

    router.post(`/teams/${team_id}/scoreboard/metrics/reorder`, {
      positions: next.map((m, i) => ({ id: m.id, position: i })),
    }, {
      // The endpoint redirects back to this meeting; reload only the
      // scoreboard props (same partial reload the metric edit save uses) so
      // the rest of the in-progress meeting state is left alone.
      preserveScroll: true,
      preserveState: true,
      only: ['scoreboard_data'],
      onError: (errors) => {
        // The endpoint answers a rejected reorder with an Inertia error
        // redirect, so this fires on a real failure. Restore the order we
        // started from rather than leaving a phantom position on screen.
        console.error('Failed to reorder metrics:', errors)
        setMetricsState(previous)
      },
    })
  }, [team_id, metricsState])

  const weekHeaders = metricsState[0]?.weekly_entries || []
  const showNumbers = !isWeekly || viewMode === 'numbers' || viewMode === 'both'
  const showWonLost = isWeekly && (viewMode === 'wonlost' || viewMode === 'both')
  const showCurrentPeriod = (showNumbers || showWonLost) && (!isWeekly || currentPeriodVisible)
  // Headers are newest-first. Use their raw period identity, never a label
  // or each metric's latest entered value. Historical columns exist in every mode.
  const scoringPeriod = isWeekly
    ? (showCurrentPeriod ? weekHeaders[0] : weekHeaders[1])?.week_ending
    : undefined

  // Recompute from the same local entries as the cells. Only the visible
  // period changes; contribution/override rules and integer-percent math do
  // not. This view calculation never writes the meeting's stored verdict.
  const localBanner = useMemo(() => {
    let win = 0
    let total = 0
    metricsState.forEach(m => {
      const e = scoringPeriod ? m.weekly_entries.find(entry => entry.week_ending === scoringPeriod) : undefined
      if (!e) return
      const manual = e.won_the_week_manual === true
      const hasValue = e.value !== null && e.value !== undefined
      if (!manual && !hasValue) return
      total += 1
      if (e.won_the_week === true) win += 1
    })

    // Task #706: the automatic verdict waits for a complete week. Every
    // weekly metric on the grid must have contributed (a value or a manual
    // Won/Lost) before a result is computed at all — scoring a half-filled
    // week just reports a loss for metrics nobody has entered yet.
    // metricsState holds exactly the team's active weekly metrics on this
    // tab, which is the same set the backend counts.
    const complete = metricsState.length > 0 && total === metricsState.length

    let won: boolean | null
    if (win_mode === 'manual') {
      won = won_the_week
    } else if (!complete) {
      won = null
    } else {
      // Integer-percent comparison, identical to the server's — a float
      // ratio against threshold/100 flips the exact-threshold case.
      won = win * 100 >= total * win_threshold
    }

    return { win, total, won, complete }
  }, [metricsState, scoringPeriod, win_mode, win_threshold, won_the_week])

  // The local recompute is authoritative — it mirrors the backend rule and
  // is sourced from the same metricsState that the cells render from. Using
  // it (rather than the static prop) keeps the banner numbers in lockstep
  // with every per-metric WON/LOST click without waiting for a full reload.
  const bannerWinCount = localBanner.win
  const bannerTotalCount = localBanner.total
  const bannerWon = localBanner.won

  // Banner visibility (task #706). Manual mode always shows it — the
  // facilitator's Won / Lost / Clear controls live there and don't depend on
  // how much of the week is entered. In automatic mode a team with no weekly
  // metrics keeps the neutral "No Metrics Reported" banner (there is nothing
  // to wait for), while a partially entered week hides it entirely until the
  // last value lands.
  const showBanner = isWeekly && (win_mode === 'manual' || metricsState.length === 0 || localBanner.complete)

  // Suppress unused-prop warnings for win_count/total_count/
  // weekly_metrics_complete — the backend still emits them (and they are the
  // server's authoritative copy of the same rule), but the banner reads the
  // local recompute above so it can update optimistically between prop
  // refreshes rather than lagging a reload behind the cell the user typed in.
  void win_count
  void total_count
  void weekly_metrics_complete

  // In-meeting "Add Metric": only when the user may create metrics for this
  // team AND we know the team id (both come from scoreboard_data props).
  // The in-meeting Add Metric modal creates weekly metrics only, so the
  // button is offered on the weekly tab only.
  const showAddMetric = can_create_metric === true && typeof team_id === 'number' && isWeekly

  // In-meeting "Edit Metric": same gate style — permission + a known team
  // id. Metric names render as plain text when the user can't edit.
  const canEditMetric = can_update_metric === true && typeof team_id === 'number'
  // Reordering is a SEPARATE permission from editing a metric: order lives on
  // the team, so the reorder endpoint authorizes TeamPolicy#update?
  // (admins/owners) while editing a metric only needs MetricPolicy#update?.
  // Gating the handle on can_update_metric would show ordinary members a
  // handle whose save the server rejects.
  // Task #716: "Create Topic" from a metric row opens the same "Add to The
  // List" pop-up The List's own Add Topic button uses, pre-filled from the
  // metric, so the facilitator reviews Topic Type/Priority/Category and the
  // owner before anything is saved. Nothing is written here.
  const handleCreateTopic = useCallback((metric: MeetingMetric) => {
    if (!meeting_id) return
    setOpenMenuMetricId(null)

    // Flush every debounced cell save now rather than leaving a just-typed
    // value sitting on a timer while the pop-up is open.
    Object.keys(pendingSaves.current).forEach(flushPendingSave)

    setTopicModalMetric(metric)
  }, [meeting_id, flushPendingSave])

  // The pop-up's create goes through the same serialized queue as the cells —
  // an independent Inertia visit would cancel a cell save still in flight (and
  // vice versa). Stays on the Scoreboard section: a partial reload of just
  // `topics` (so The List picks it up) and `flash`, with the query string
  // carried so the redirect rebuilds the same cadence/period view.
  const topicCreateSeq = useRef(0)
  const submitTopic = useCallback((
    payload: TopicCreatePayload,
    { onSuccess, onError }: TopicSubmitHandlers,
  ) => {
    if (!meeting_id) return

    // Anything still debouncing goes into the queue ahead of this create
    // instead of being stranded behind it unsent.
    Object.keys(pendingSaves.current).forEach(flushPendingSave)

    const title = String(payload.topic.title ?? '')
    // A unique key per submission: "Save and Create Another" must never fold
    // a second topic into a first one that is still waiting its turn.
    topicCreateSeq.current += 1

    enqueue({
      key: `topic:create:${topicCreateSeq.current}`,
      url: `/meetings/${meeting_id}/topics${window.location.search}`,
      only: ['topics', 'flash'],
      body: payload as unknown as Record<string, unknown>,
      onSuccess: () => {
        showTopicStatus('success', `“${title}” added to The List.`)
        onSuccess()
      },
      onError: (detail) => {
        showTopicStatus(
          'error',
          detail
            ? `Could not add “${title}” to The List: ${detail}.`
            : `Could not add “${title}” to The List. Please try again.`,
        )
        onError(detail)
      },
    })
  }, [meeting_id, enqueue, flushPendingSave, showTopicStatus])

  // Owner pre-fill: the metric's owner, but only when that person is actually
  // selectable on this team's member list (the meeting serializer uses owner
  // id 0 for "Unassigned"). Otherwise leave it unset rather than guessing.
  const topicModalOwnerId = useMemo<number | ''>(() => {
    const ownerId = topicModalMetric?.owner?.id
    if (!ownerId) return ''
    return (teamMembers || []).some(m => m.id === ownerId) ? ownerId : ''
  }, [topicModalMetric, teamMembers])

  const canReorder = can_reorder_metric === true && typeof team_id === 'number'
  // Row actions menu (task #644). Its only item creates a topic, so it is
  // offered on the same rule as the List of Topics "Add Topic" button: a
  // facilitator of a live meeting. Others get the column, without a trigger.
  const showRowMenu = canCreateTopic === true && typeof meeting_id === 'number'
  const editMetric = editMetricId !== null ? (metricsState.find(m => m.id === editMetricId) || null) : null
  // Adapt a MeetingMetric to the modal's EditableMetric shape (owner id 0
  // means "Unassigned" in the meeting serializer).
  const editableMetric = editMetric ? {
    id: editMetric.id,
    name: editMetric.name,
    description: editMetric.description ?? null,
    owner: editMetric.owner && editMetric.owner.id !== 0 ? { id: editMetric.owner.id } : null,
    frequency: editMetric.frequency ?? 'weekly',
    format: editMetric.format ?? null,
    comparison: editMetric.comparison ?? null,
    target: editMetric.target ?? null,
    category_id: editMetric.category_id ?? null,
    decimal_places: editMetric.decimal_places ?? 0,
    abbreviate_large_numbers: editMetric.abbreviate_large_numbers ?? true,
  } : null

  const editMetricModal = canEditMetric ? (
    <EditMetricModal
      isOpen={editableMetric !== null}
      onClose={() => setEditMetricId(null)}
      metric={editableMetric}
      teamId={team_id as number}
      teamMembers={teamMembers || []}
      categories={categories || []}
      preserveState
      only={['scoreboard_data']}
    />
  ) : null

  const addMetricButton = showAddMetric ? (
    <button
      onClick={() => setAddMetricOpen(true)}
      className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-1.5 px-3 rounded-lg transition-colors font-heading text-sm flex items-center gap-1.5"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      Add Metric
    </button>
  ) : null

  // Rendered whenever the row menu is offered, so it can stay mounted (and
  // keep the user's entries) across the meeting's heartbeat prop refreshes.
  const createTopicModal = showRowMenu ? (
    <AddTopicModal
      isOpen={topicModalMetric !== null}
      onClose={() => setTopicModalMetric(null)}
      meetingId={meeting_id as number}
      teamMembers={teamMembers || []}
      currentUserId={currentUserId}
      initialTitle={topicModalMetric?.name ?? ''}
      initialOwnerId={topicModalOwnerId}
      source={topicModalMetric ? { type: 'Metric', id: topicModalMetric.id } : null}
      onSubmit={submitTopic}
    />
  ) : null

  const addMetricModal = showAddMetric ? (
    <AddMetricModal
      isOpen={addMetricOpen}
      onClose={() => setAddMetricOpen(false)}
      teamId={team_id as number}
      teamMembers={teamMembers || []}
      categories={categories || []}
      preserveState
      weeklyOnly
    />
  ) : null

  const periodNoun = CADENCE_PERIOD_NOUN[cadence]
  const subtitle = isWeekly
    ? 'Win the week — review weekly metrics.'
    : `Review ${cadence} metrics.`

  const cadenceTabs = (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap items-center gap-1 bg-charcoal/5 rounded-lg p-1 w-fit">
        {CADENCE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => handleCadenceChange(opt.value)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              cadence === opt.value
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {!isWeekly && (
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <span className="font-medium">Show:</span>
          <select
            value={periods}
            onChange={(e) => handlePeriodsChange(Number(e.target.value))}
            className="border border-charcoal/20 rounded-md px-2 py-1.5 text-sm text-text-primary bg-white"
          >
            {PERIOD_COUNT_OPTIONS.map(n => (
              <option key={n} value={n}>Last {n} {periodNounPlural(cadence, n)}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  )

  if (metricsState.length === 0) {
    return (
      <div>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Scoreboard</h3>
            <p className="text-base text-text-muted">{subtitle}</p>
          </div>
          {addMetricButton}
        </div>
        {cadenceTabs}
        {showBanner && (
          <WinTheWeekBanner won_the_week={bannerWon} win_count={bannerWinCount} total_count={bannerTotalCount} win_mode={win_mode} win_threshold={win_threshold} meeting_id={meeting_id} can_facilitate={can_facilitate} />
        )}
        <EmptyState
          title={isWeekly ? 'No Metrics Configured' : `No ${periodNoun}ly Metrics`.replace('Yearly', 'Annual')}
          description={isWeekly
            ? "Add metrics to your team's scoreboard to track weekly performance here."
            : `This team has no ${cadence} metrics yet. Add them from the team Scoreboard page to review them here.`}
        />
        {addMetricModal}
        {editMetricModal}
      </div>
    )
  }

  // The metric column pins to the left of the scroll container; when the
  // drag handle column is rendered it takes the left edge and the metric
  // column slides over by its width.
  const HANDLE_COL_WIDTH = 32
  const metricColLeft = canReorder ? HANDLE_COL_WIDTH : 0

  // Horizontal scrolling for the wider weekly windows (task #707). Past-week
  // columns get a floor width and stop wrapping, and the table is given a
  // minimum width that grows with the column count so 8 and 12 weeks push a
  // scrollbar into the existing scroll container instead of compressing
  // every column. Left untouched at 4 weeks so the default grid still
  // stretches to fill the section exactly as before.
  const PAST_COL_MIN_WIDTH = 110
  // Metric + Owner + Target + YTD + This Week + the sticky actions column.
  const FIXED_COLS_WIDTH = 200 + 120 + 100 + 100 + 120 + 44
  // Collapsing the current-period column (task #703) hands its share of the
  // fixed width back to the past weeks; leaving it in the floor would keep a
  // scrollbar the hidden column no longer justifies.
  const CURRENT_COL_WIDTH = 120
  const pastColumnCount = Math.max(weekHeaders.length - 1, 0)
  const tableMinWidth = isWeekly && weekHeaders.length > 4
    ? FIXED_COLS_WIDTH - (showCurrentPeriod ? 0 : CURRENT_COL_WIDTH) + metricColLeft + pastColumnCount * PAST_COL_MIN_WIDTH
    : undefined
  const pastColStyle: React.CSSProperties = tableMinWidth
    ? { minWidth: `${PAST_COL_MIN_WIDTH}px`, whiteSpace: 'nowrap' }
    : { whiteSpace: 'nowrap' }

  type RowDrag = {
    innerRef: (el: HTMLElement | null) => void
    draggableProps: DraggableProvidedDraggableProps
    dragHandleProps: DraggableProvidedDragHandleProps | null
    isDragging: boolean
  }

  const renderMetricRow = (metric: MeetingMetric, drag?: RowDrag) => {
    const currentEntry = metric.weekly_entries[0]
    const currentWeekEnding = currentEntry?.week_ending || ''
    const currentKey = cellKey(metric.id, currentWeekEnding)
    const isCurrentSaving = savingCells.has(currentKey)
    const rowBg = drag?.isDragging ? '#F5F5F5' : '#fff'

    return (
      <tr
        key={metric.id}
        ref={drag?.innerRef}
        {...(drag?.draggableProps || {})}
        style={{
          ...(drag?.draggableProps?.style || {}),
          ...(drag?.isDragging ? { display: 'table', boxShadow: '0 6px 16px rgba(0,0,0,0.15)', backgroundColor: rowBg } : {}),
        }}
      >
        {canReorder && (
          <td style={{ ...cellStyle, ...handleColStyle, backgroundColor: rowBg }}>
            <div
              {...(drag?.dragHandleProps || {})}
              aria-label="Drag to reorder"
              className="flex items-center justify-center text-text-muted hover:text-text-primary"
              style={{ cursor: 'grab', touchAction: 'none' }}
            >
              {/* Six-dot handle — identical to Rock Review's. */}
              <svg className="w-4 h-4 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 4a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm6-10a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2z" />
              </svg>
            </div>
          </td>
        )}
        <td style={{ ...cellStyle, ...stickyColStyle, left: metricColLeft, backgroundColor: rowBg, fontWeight: 600, maxWidth: '200px' }}>
          {canEditMetric ? (
            <button
              onClick={() => setEditMetricId(metric.id)}
              title={metric.name}
              className="font-semibold text-text-primary hover:text-pinnacle-red transition-colors text-left"
              style={{ fontFamily: 'inherit', fontSize: 'inherit', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              {metric.name}
            </button>
          ) : (
            metric.name
          )}
        </td>
        <td style={cellStyle}>{metric.owner?.first_name || 'Unassigned'}</td>
        <td style={{ ...cellStyle, textAlign: 'right', color: '#1B1B1B' }}>
          {/* Double-click edits the target in place (task #650), behind the
              same permission gate as the clickable metric name. */}
          <InlineTargetCell
            value={metric.target ?? null}
            display={formatValue(metric.target_value, metric)}
            canEdit={canEditMetric}
            isSaving={savingTargets.has(metric.id)}
            onSave={(raw) => handleTargetSave(metric, raw)}
            ariaLabel={`Target for ${metric.name}`}
            error={targetErrors[metric.id] ?? null}
            align="right"
            inputWidth={80}
            suffix={
              <>
                {metric.goal_direction === 'higher_is_better' && <span style={{ fontSize: '11px', marginLeft: '2px' }}>+</span>}
                {metric.goal_direction === 'lower_is_better' && <span style={{ fontSize: '11px', marginLeft: '2px' }}>−</span>}
              </>
            }
          />
        </td>
        {showCurrentPeriod && (
          <td style={{ ...cellStyle, textAlign: 'right', padding: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              {showNumbers && (
                <MetricValueInput
                  unit={metric.unit}
                  value={currentEntry?.value ?? null}
                  formattedValue={formatValue(currentEntry?.value ?? null, metric)}
                  onTarget={(currentEntry?.won_the_week ?? currentEntry?.on_target) ?? null}
                  isSaving={isCurrentSaving}
                  onChange={(v) => handleCurrentValueChange(metric.id, currentWeekEnding, v)}
                  inputRef={(el) => { valueInputRefs.current[`${metric.id}:0`] = el }}
                  onEnter={(el) => handleValueEnter(metric.id, 0, currentKey, el)}
                />
              )}
              {showWonLost && (
                <WonLostToggle
                  value={currentEntry?.won_the_week ?? null}
                  manual={currentEntry?.won_the_week_manual === true}
                  onChange={(newVal) => handleWonLostToggle(metric.id, currentWeekEnding, currentEntry?.won_the_week ?? null, newVal)}
                  isSaving={isCurrentSaving}
                  compact
                />
              )}
            </div>
          </td>
        )}
        {metric.weekly_entries.slice(1).map((entry, pastIdx) => {
          const colIndex = pastIdx + 1
          const key = cellKey(metric.id, entry.week_ending)
          const cellSaving = savingCells.has(key)
          // Past periods are editable on the weekly tab only
          // (task #68 window rules). Other cadences show prior
          // periods read-only; editing is current-period only.
          if (!isWeekly) {
            const state = entry.won_the_week ?? entry.on_target
            const color = state === true ? '#15532E' : state === false ? '#ED1C24' : '#1B1B1B'
            return (
              <td key={entry.week_ending} style={{ ...cellStyle, ...pastColStyle, textAlign: 'right', padding: '8px', fontWeight: 700, color }}>
                {entry.value !== null && entry.value !== undefined ? formatValue(entry.value, metric) : '—'}
              </td>
            )
          }
          return (
            <td key={entry.week_ending} style={{ ...cellStyle, ...pastColStyle, textAlign: 'right', padding: '8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                {showNumbers && <MetricValueInput
                  unit={metric.unit}
                  value={entry.value}
                  formattedValue={formatValue(entry.value, metric)}
                  onTarget={(entry.won_the_week ?? entry.on_target) ?? null}
                  isSaving={cellSaving}
                  onChange={(v) => handlePastValueChange(metric.id, entry.week_ending, entry.week_start, v)}
                  inputRef={(el) => { valueInputRefs.current[`${metric.id}:${colIndex}`] = el }}
                  onEnter={(el) => handleValueEnter(metric.id, colIndex, key, el)}
                />}
                {showWonLost && (
                  <WonLostToggle
                    value={entry.won_the_week ?? null}
                    manual={entry.won_the_week_manual === true}
                    onChange={(newVal) => handlePastWonLostToggle(metric.id, entry.week_ending, entry.week_start, entry.won_the_week ?? null, newVal)}
                    isSaving={cellSaving}
                    compact
                  />
                )}
              </div>
            </td>
          )
        })}
        <td style={{ ...cellStyle, ...actionsColStyle, backgroundColor: rowBg }}>
          {showRowMenu && (
            <ScoreboardRowMenu
              metricName={metric.name}
              open={openMenuMetricId === metric.id}
              onToggle={() => setOpenMenuMetricId(prev => (prev === metric.id ? null : metric.id))}
              onClose={() => setOpenMenuMetricId(prev => (prev === metric.id ? null : prev))}
              onCreateTopic={() => handleCreateTopic(metric)}
            />
          )}
        </td>
      </tr>
    )
  }

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Scoreboard</h3>
          <p className="text-base text-text-muted">{subtitle}</p>
        </div>
        {addMetricButton}
      </div>
      {cadenceTabs}
      {showBanner && (
        <WinTheWeekBanner won_the_week={bannerWon} win_count={bannerWinCount} total_count={bannerTotalCount} win_mode={win_mode} win_threshold={win_threshold} meeting_id={meeting_id} can_facilitate={can_facilitate} />
      )}

      {isWeekly && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {/* How many weekly columns to render (task #707). Sits beside the
            Week starts dropdown and wears the same treatment so the two read
            as a pair. Outside the team_id gate: unlike Week starts / Dates
            (which persist a team setting) this is view-only URL state. */}
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Weeks shown:</span>
        <select
          aria-label="Weeks shown"
          value={weeklyPeriods}
          onChange={(e) => handlePeriodsChange(Number(e.target.value))}
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '13px',
            fontWeight: 500,
            color: '#1B1B1B',
            backgroundColor: '#F5F5F5',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 10px',
            cursor: 'pointer',
          }}
        >
          {WEEKLY_PERIOD_COUNT_OPTIONS.map(n => (
            <option key={n} value={n}>{n} weeks</option>
          ))}
        </select>
        <span style={{ width: '12px' }} />
        {typeof team_id === 'number' && (
          <>
            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Week starts:</span>
            <select
              value={weekStartDay}
              onChange={(e) => handleWeekStartDayChange(e.target.value)}
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1B1B1B',
                backgroundColor: '#F5F5F5',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => (
                <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
              ))}
            </select>
            <span style={{ width: '12px' }} />
            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dates:</span>
            {([
              { key: 'week_start' as const, label: 'Week Starting' },
              { key: 'week_ending' as const, label: 'Week Ending' },
            ]).map(opt => (
              <button
                key={opt.key}
                onClick={() => handleWeekLabelChange(opt.key)}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  fontWeight: weekLabel === opt.key ? 700 : 500,
                  color: weekLabel === opt.key ? '#fff' : '#1B1B1B',
                  backgroundColor: weekLabel === opt.key ? '#1B1B1B' : '#F5F5F5',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {opt.label}
              </button>
            ))}
            <span style={{ width: '12px' }} />
          </>
        )}
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>View:</span>
        {(['both', 'numbers', 'wonlost'] as ViewMode[]).map(mode => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '13px',
              fontWeight: viewMode === mode ? 700 : 500,
              color: viewMode === mode ? '#fff' : '#1B1B1B',
              backgroundColor: viewMode === mode ? '#1B1B1B' : '#F5F5F5',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {mode === 'both' ? 'Both' : mode === 'numbers' ? 'Numbers' : 'Won / Lost'}
          </button>
        ))}
        <span style={{ width: '12px' }} />
        {/* Display-only column toggle (task #703). Same pill treatment as the
            Dates / View groups above so the row reads as one set of controls;
            aria-pressed carries the on/off state to assistive tech. */}
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#1B1B1B', marginRight: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Column:</span>
        <button
          type="button"
          onClick={() => setCurrentPeriodVisible(v => !v)}
          aria-pressed={currentPeriodVisible}
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '13px',
            fontWeight: currentPeriodVisible ? 700 : 500,
            color: currentPeriodVisible ? '#fff' : '#1B1B1B',
            backgroundColor: currentPeriodVisible ? '#1B1B1B' : '#F5F5F5',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 12px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          This Week
        </button>
      </div>
      )}

      {topicStatus && (
        <div
          role="status"
          className={`mb-3 px-4 py-2 rounded-lg text-sm ${
            topicStatus.kind === 'success'
              ? 'bg-[#15532E]/10 text-[#15532E]'
              : 'bg-[#ED1C24]/10 text-[#ED1C24]'
          }`}
        >
          {topicStatus.text}
        </div>
      )}

      {/* Bounded-height scroll container so both sticky axes work: the
          header row pins to the top and the metric column pins to the
          left while the rest of the grid scrolls underneath. */}
      <div style={{ overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              {canReorder && (
                <th style={{ ...headerStyle, ...stickyHeaderStyle, ...handleColStyle, padding: '12px 4px', zIndex: 5 }} />
              )}
              <th style={{ ...headerStyle, ...stickyHeaderStyle, ...stickyColStyle, left: metricColLeft, zIndex: 5 }}>Metric</th>
              <th style={{ ...headerStyle, ...stickyHeaderStyle }}>Owner</th>
              <th style={{ ...headerStyle, ...stickyHeaderStyle, textAlign: 'right' }}>Target</th>
              {showCurrentPeriod && (
                <th style={{ ...headerStyle, ...stickyHeaderStyle, textAlign: 'right' }}>This {periodNoun}</th>
              )}
              {weekHeaders.slice(1).map((entry) => (
                <th key={entry.week_ending} style={{ ...headerStyle, ...stickyHeaderStyle, ...pastColStyle, textAlign: 'right' }}>
                  {entry.label || formatWeekDate(weekLabel === 'week_ending' ? entry.week_ending : entry.week_start)}
                </th>
              ))}
              {/* Row actions. Rendered even when the menu isn't offered so
                  the grid's column widths don't shift between viewers. */}
              <th style={{ ...headerStyle, ...stickyHeaderStyle, ...actionsColStyle, zIndex: 5 }}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          {canReorder ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="meeting-scoreboard-metrics" type="meeting-metric">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {metricsState.map((metric: MeetingMetric, index: number) => (
                      <Draggable key={metric.id} draggableId={String(metric.id)} index={index}>
                        {(prov, snapshot) => renderMetricRow(metric, {
                          innerRef: prov.innerRef,
                          draggableProps: prov.draggableProps,
                          dragHandleProps: prov.dragHandleProps,
                          isDragging: snapshot.isDragging,
                        })}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <tbody>
              {metricsState.map((metric: MeetingMetric) => renderMetricRow(metric))}
            </tbody>
          )}
        </table>
      </div>
      {addMetricModal}
      {editMetricModal}
      {createTopicModal}
    </div>
  )
}

const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: '#fff',
  borderBottom: '2px solid rgba(27,27,27,0.2)',
}
// Sticky-right column holding the per-row actions menu (task #644).
const actionsColStyle: React.CSSProperties = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  backgroundColor: '#fff',
  width: '44px',
  minWidth: '44px',
  maxWidth: '44px',
  padding: '8px 4px',
  textAlign: 'center',
  boxShadow: '-4px 0 8px -2px rgba(0,0,0,0.08)',
}

const headerStyle: React.CSSProperties = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: '#1B1B1B',
  padding: '12px 8px',
  textAlign: 'left',
}

const cellStyle: React.CSSProperties = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: '14px',
  color: '#1B1B1B',
  padding: '16px 8px',
  borderBottom: '1px solid #E6E6E6',
}


const stickyColStyle: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: '#fff',
  boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)',
}

const handleColStyle: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: '#fff',
  width: '32px',
  minWidth: '32px',
  padding: '16px 4px',
}
